import { faArrowDown, faArrowUp, faBox, faCalendarDay, faCartShopping, faFutbol, faHatChef, faMoneyBillTrendUp, faPeopleGroup, faQuestion, faShieldHalved, faSkullCrossbones, faUserLargeSlash, faWarning } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { ClubContext } from "src/app/helpers/ClubContext";
import { Col, Row } from "react-bootstrap";
import { CardType } from "src/app/enums/Index";
import { FormatNumber, safeJson } from "src/app/helpers/Index";
import { Card, Fixture, Goal, Player, Substitute, Team } from "src/app/interfaces/Index";
import { CardService, FixtureService, GoalService, PlayerService, TeamService } from "src/app/services/Index";
import { SubstituteService } from "src/app/services/SubstituteService";
import { colours } from "src/app/Styles";
import "./AdminDashboard.css";

type Props = {};
type State = {
	nextFixtureDate: string;
	remainingFixtures: number;
	salesOverview: any[];
	seasonStats: {name: string, value: number}[];
	seasonStatsMax: number;
	topScorers: {goals: number, mugshotUrl: string, name: string, playerNumber: number}[];
	totalPlayers: number;
	totalTeams: number;
	upcomingOrders: any[];
};
export class AdminDashboard extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {nextFixtureDate: "-", remainingFixtures: 0, salesOverview: [], seasonStats: [], seasonStatsMax: 0, topScorers: [], totalPlayers: 0, totalTeams: 0, upcomingOrders: []};
	}

	componentDidMount() {
		this.getSalesOverview();
		this.getStats();
		this.getUpcomingOrders();
	}

	async getStats() {
		const fixtures: Fixture[] = await safeJson(FixtureService.getAll());
		const players: Player[] = await safeJson(PlayerService.getAll());
		const teams: Team[] = await safeJson(TeamService.getAll());
		const goals: Goal[] = await safeJson(GoalService.getAll());
		const subs: Substitute[] = await safeJson(SubstituteService.getAll());
		const cards: Card[] = await safeJson(CardService.getAll());

		const now = new Date();
		const allFixtures = Array.isArray(fixtures) ? fixtures : [];
		const allPlayers = Array.isArray(players) ? players : [];
		const allGoals = Array.isArray(goals) ? goals : [];
		const allSubs = Array.isArray(subs) ? subs : [];
		const allCards = Array.isArray(cards) ? cards : [];

		// The season runs 31 Jul -> 31 Jul; stats only count fixtures inside the current window
		const seasonStart = new Date(now >= new Date(now.getFullYear(), 6, 31) ? now.getFullYear() : now.getFullYear() - 1, 6, 31);
		const seasonEnd = new Date(seasonStart.getFullYear() + 1, 6, 31);
		const seasonFixtures = allFixtures.filter((x) => new Date(x.fixtureDateTime) >= seasonStart && new Date(x.fixtureDateTime) < seasonEnd);
		const seasonFixtureIds = new Set(seasonFixtures.map((x) => x.id));

		const upcoming = seasonFixtures
			.filter((x) => new Date(x.fixtureDateTime) >= now)
			.sort((a, b) => new Date(a.fixtureDateTime).getTime() - new Date(b.fixtureDateTime).getTime());

		// Number() casts: the deployed PlayerDto serialises teamId as a string
		const homePlayerIds = new Set(allPlayers.filter((x) => Number(x.teamId) === ClubContext.homeTeamId).map((x) => x.id));
		const played = seasonFixtures.filter((x) => new Date(x.fixtureDateTime) < now);
		const minutesPlayed = played.reduce((total, x) => total + 90 + (x.firstHalfAdditional || 0) + (x.secondHalfAdditional || 0), 0);

		const seasonStats = [
			{name: "Home Goals", value: allGoals.filter((x) => seasonFixtureIds.has(x.fixtureId) && homePlayerIds.has(x.playerId)).length},
			{name: "Substitutions", value: allSubs.filter((x) => seasonFixtureIds.has(x.fixtureId)).length},
			{name: "Yellow Cards", value: allCards.filter((x) => seasonFixtureIds.has(x.fixtureId) && x.cardType === CardType.Yellow).length},
			{name: "Red Cards", value: allCards.filter((x) => seasonFixtureIds.has(x.fixtureId) && x.cardType === CardType.Red).length},
			{name: "Minutes Played", value: minutesPlayed}
		];

		const playerById = new Map(allPlayers.map((x) => [x.id, x]));
		const goalCounts = new Map<number, number>();
		allGoals
			.filter((x) => seasonFixtureIds.has(x.fixtureId) && homePlayerIds.has(x.playerId))
			.forEach((x) => goalCounts.set(x.playerId, (goalCounts.get(x.playerId) || 0) + 1));
		const topScorers = Array.from(goalCounts.entries())
			.map(([playerId, goalTotal]) => ({
				goals: goalTotal,
				mugshotUrl: playerById.get(playerId)?.mugshotUrl || "",
				name: playerById.get(playerId)?.playerName || "Unknown",
				playerNumber: playerById.get(playerId)?.playerNumber || 0
			}))
			.sort((a, b) => b.goals - a.goals)
			.slice(0, 10);

		this.setState({
			nextFixtureDate: upcoming.length > 0 ? new Date(upcoming[0].fixtureDateTime).toLocaleDateString("en-GB", {day: "2-digit", month: "2-digit", year: "numeric"}) : "TBC",
			remainingFixtures: upcoming.length,
			seasonStats,
			seasonStatsMax: Math.max(...seasonStats.filter((x) => x.name !== "Minutes Played").map((x) => x.value), 1),
			topScorers,
			totalPlayers: allPlayers.filter((x) => Number(x.teamId) === ClubContext.homeTeamId).length,
			totalTeams: Array.isArray(teams) ? teams.length : 0
		});
	}

	getNumericColor(value: number) {
		switch (value) {
			case 0:
				return colours.graphs.primary;
			case 1:
				return colours.graphs.secondary;
			case 2:
				return colours.graphs.tertiary;
			case 3:
				return colours.graphs.quaternary;
			default:
				return colours.graphs.primary;
		}
	}

	getSalesOverview() {
		const data = [{}];
		this.setState({salesOverview: data});
	}

	getUpcomingOrders() {
		const data = [{}];
		this.setState({upcomingOrders: data});
	}

	render() {
		return (
			<>
				<Row>
					<Col xs={12}>
						<div className="card" style={{background: "linear-gradient(120deg, #7A2039 0%, #932A47 60%, #A83249 100%)", marginBottom: "1.5rem", overflow: "hidden", padding: "2.5rem 2.5rem", position: "relative"}}>
							<img alt="" src="/assets/pitchbase-icon.png" style={{bottom: "-3rem", height: "16rem", opacity: 0.14, position: "absolute", right: "-2rem", transform: "rotate(-8deg)"}}/>
							<div className="d-flex align-items-center justify-content-between" style={{position: "relative"}}>
								<div>
									<h1 style={{color: "#FFFFFF", fontSize: "2.2rem", fontWeight: 800, marginBottom: "0.5rem"}}>Welcome, Scunthorpe United</h1>
									<div style={{color: "rgba(255, 255, 255, 0.85)", fontSize: "1.05rem", fontWeight: 600, maxWidth: "42rem"}}>
										Welcome to Pitchbase — your matchday scoreboard, sponsors, visitors and club admin, all in one place.
									</div>
								</div>
								<img alt="Club badge" src="/assets/Badge.png" style={{filter: "drop-shadow(0 8px 16px rgba(0, 0, 0, 0.25))", height: "6.5rem"}}/>
							</div>
						</div>
					</Col>
				</Row>
				<Row>
					<Col lg={6} xl={3} xs={12}>
						<div className="card" style={{marginBottom: "1.5rem", padding: "1.5rem"}}>
							<div className="d-flex justify-content-between mb-3">
								<div>
									<span className="mb-3" style={{color: colours.quaternary, fontWeight: 500}}>Next Fixture</span>
									<div style={{fontSize: "1.25rem", fontWeight: 500}}>{this.state.nextFixtureDate}</div>
								</div>
								<div className="d-flex align-items-center justify-content-center" style={{backgroundColor: colours.graphs.primaryBg, height: "2.5rem", width: "2.5rem"}}>
									<FontAwesomeIcon icon={faCalendarDay} style={{color: colours.graphs.primary, fontSize: "1.25rem"}}/>
								</div>
							</div>
						</div>
					</Col>
					<Col lg={6} xl={3} xs={12}>
						<div className="card" style={{marginBottom: "1.5rem", padding: "1.5rem"}}>
							<div className="d-flex justify-content-between mb-3">
								<div>
								<span className="mb-3" style={{color: colours.quaternary, fontWeight: 500}}>Remaining Fixtures</span>
									<div style={{fontSize: "1.25rem", fontWeight: 500}}>{this.state.remainingFixtures}</div>
								</div>
								<div className="d-flex align-items-center justify-content-center" style={{backgroundColor: colours.graphs.secondaryBg, height: "2.5rem", width: "2.5rem"}}>
									<FontAwesomeIcon icon={faFutbol} style={{color: colours.graphs.secondary, fontSize: "1.25rem"}}/>
								</div>
							</div>
						</div>
					</Col>
					<Col lg={6} xl={3} xs={12}>
						<div className="card" style={{marginBottom: "1.5rem", padding: "1.5rem"}}>
							<div className="d-flex justify-content-between mb-3">
								<div>
								<span className="mb-3" style={{color: colours.quaternary, fontWeight: 500}}>Total Players</span>
									<div style={{fontSize: "1.25rem", fontWeight: 500}}>{this.state.totalPlayers}</div>
								</div>
								<div className="d-flex align-items-center justify-content-center" style={{backgroundColor: colours.graphs.tertiaryBg, height: "2.5rem", width: "2.5rem"}}>
									<FontAwesomeIcon icon={faBox} style={{color: colours.graphs.tertiary, fontSize: "1.25rem"}}/>
								</div>
							</div>
						</div>
					</Col>
					<Col lg={6} xl={3} xs={12}>
						<div className="card" style={{marginBottom: "1.5rem", padding: "1.5rem"}}>
							<div className="d-flex justify-content-between mb-3">
								<div>
								<span className="mb-3" style={{color: colours.quaternary, fontWeight: 500}}>Total Teams</span>
									<div style={{fontSize: "1.25rem", fontWeight: 500}}>{this.state.totalTeams}</div>
								</div>
								<div className="d-flex align-items-center justify-content-center" style={{backgroundColor: colours.graphs.quaternaryBg, height: "2.5rem", width: "2.5rem"}}>
									<FontAwesomeIcon icon={faShieldHalved} style={{color: colours.graphs.quaternary, fontSize: "1.25rem"}}/>
								</div>
							</div>
						</div>
					</Col>
				</Row>
				<Row>
					<Col xl={6} xs={12}>
						<div className="card" style={{padding: "1.5rem"}}>
							<h5>Season Stats</h5>
							<ul className="list-none" style={{marginBottom: 0, marginTop: 25, padding: 0}}>
								{this.state.seasonStats.map((stat, i) =>
									<li className="d-flex flex-row align-items-center justify-content-between mb-4" key={`seasonStats-${i}`}>
										<div>
											<span className="text-900 font-medium mr-2 mb-1">{stat.name}</span>
										</div>
										<div className="my-auto">
											<div style={{backgroundColor: colours.quinary, display: "inline-block", height: 8, margin: "3px 0px 3px 0px", width: 150}}>
												<div style={{backgroundColor: this.getNumericColor(i % 4), height: 8, width: `${Math.min(100, (100 / this.state.seasonStatsMax) * stat.value)}%`}}/>
											</div>
											<span style={{color: this.getNumericColor(i % 4), display: "inline-block", fontSize: 20, fontWeight: "bold", textAlign: "right", width: 60}}>{FormatNumber(stat.value)}</span>
										</div>
									</li>
								)}
							</ul>
						</div>
					</Col>
					<Col xl={6} xs={12}>
						{this.renderTopScorers()}
					</Col>
				</Row>
			</>
		);
	}

	renderTopScorers() {
		return (
			<div className="card" style={{padding: "1.5rem"}}>
				<h5>Top Scorers</h5>
				{this.state.topScorers.length === 0 &&
					<div style={{color: colours.quaternary, fontWeight: 500, marginTop: 24}}>No goals scored yet this season.</div>
				}
				<ul className="list-none" style={{marginBottom: 0, marginTop: 25, padding: 0}}>
					{this.state.topScorers.map((scorer, i) =>
						<li className="d-flex flex-row align-items-center mb-3" key={`topScorer-${i}`}>
							<span style={{color: i === 0 ? "#11B981" : colours.quaternary, fontSize: 18, fontWeight: 800, textAlign: "center", width: 32}}>{i + 1}</span>
							{scorer.mugshotUrl
								? <img alt={scorer.name} src={scorer.mugshotUrl} style={{borderRadius: "50%", height: 40, marginLeft: 8, marginRight: 12, objectFit: "cover", objectPosition: "top", width: 40}}/>
								: <div className="d-flex align-items-center justify-content-center" style={{backgroundColor: colours.quinary, borderRadius: "50%", color: colours.quaternary, fontWeight: 800, height: 40, marginLeft: 8, marginRight: 12, width: 40}}>{scorer.playerNumber}</div>
							}
							<span className="text-900 font-medium" style={{flex: 1}}>{scorer.name}</span>
							<span style={{color: i === 0 ? "#11B981" : colours.septenary, fontSize: 20, fontWeight: "bold"}}>{scorer.goals}</span>
						</li>
					)}
				</ul>
			</div>
		);
	}
}